import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Statistic,
  Row,
  Col,
  Typography,
  Switch,
  Divider,
  Alert,
  App,
  List,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DragOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

import {
  useGetPaymentMethodsQuery,
  useGetPaymentMethodSummaryQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useUpdatePaymentMethodStatusMutation,
  useDeletePaymentMethodMutation,
  useReorderPaymentMethodsMutation,
} from '../../services/paymentMethodsApi';
import { PaymentMethod } from '../../types/payments';
import PaymentMethodFormModal from './modals/PaymentMethodFormModal';
import SortableTableRow from './SortableTableRow';
import { useResponsive } from '../../hooks/useResponsive';

const { Text } = Typography;

const PaymentMethodsManager: React.FC = () => {
  // Get modal and message instances from App context
  const { modal, message: messageApi } = App.useApp();
  const { isMobile } = useResponsive();

  // State for modals and editing
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);

  // API hooks
  const {
    data: paymentMethods = [],
    isLoading: methodsLoading,
    refetch: refetchMethods
  } = useGetPaymentMethodsQuery({ activeOnly: false });

  const {
    data: summary,
    isLoading: summaryLoading
  } = useGetPaymentMethodSummaryQuery();

  // Mutations
  const [createPaymentMethod, { isLoading: createLoading }] = useCreatePaymentMethodMutation();
  const [updatePaymentMethod, { isLoading: updateLoading }] = useUpdatePaymentMethodMutation();
  const [updatePaymentMethodStatus] = useUpdatePaymentMethodStatusMutation();
  const [deletePaymentMethod] = useDeletePaymentMethodMutation();
  const [reorderPaymentMethods] = useReorderPaymentMethodsMutation();

  // Handlers
  const handleAddMethod = () => {
    setEditingMethod(undefined);
    setFormModalVisible(true);
  };

  const handleEditMethod = (method: PaymentMethod) => {
    setEditingMethod(method);
    setFormModalVisible(true);
  };

  const handleFormSubmit = async (values: any) => {
    try {
      if (editingMethod) {
        await updatePaymentMethod({ id: editingMethod.id, ...values }).unwrap();
        messageApi.success('Forma de pago actualizada exitosamente');
      } else {
        await createPaymentMethod(values).unwrap();
        messageApi.success('Forma de pago creada exitosamente');
      }
      setFormModalVisible(false);
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al guardar forma de pago');
    }
  };

  const handleStatusToggle = async (method: PaymentMethod, checked: boolean) => {
    try {
      await updatePaymentMethodStatus({
        id: method.id,
        status: { isActive: checked }
      }).unwrap();
      messageApi.success(`Forma de pago ${checked ? 'activada' : 'desactivada'} exitosamente`);
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al cambiar estado');
    }
  };

  const handleDeleteMethod = (method: PaymentMethod) => {
    modal.confirm({
      title: '¿Eliminar forma de pago?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>¿Estás seguro de eliminar la forma de pago <strong>{method.name}</strong>?</p>
          <p style={{ color: 'var(--color-error-text)', fontSize: '12px' }}>
            Esta acción no se puede deshacer y puede afectar órdenes existentes.
          </p>
        </div>
      ),
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deletePaymentMethod(method.id).unwrap();
          messageApi.success('Forma de pago eliminada exitosamente');
        } catch (error: any) {
          console.error('❌ Delete error:', error);
          messageApi.error(error?.data?.message || 'Error al eliminar forma de pago');
        }
      },
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragging(false);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = paymentMethods.findIndex(method => method.id === active.id);
    const newIndex = paymentMethods.findIndex(method => method.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Create new array with reordered items
    const newPaymentMethods = [...paymentMethods];
    const [movedItem] = newPaymentMethods.splice(oldIndex, 1);
    newPaymentMethods.splice(newIndex, 0, movedItem);

    try {
      // Extract the ordered IDs
      const orderedIds = newPaymentMethods.map(method => method.id);
      await reorderPaymentMethods(orderedIds).unwrap();
      messageApi.success('Orden actualizado exitosamente');
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al reordenar formas de pago');
    }
  };

  const columns: ColumnsType<PaymentMethod> = [
    {
      key: 'sort',
      width: 30,
      render: () => (
        <DragOutlined 
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            color: 'var(--color-neutral-text)'
          }} 
        />
      ),
    },
    {
      title: 'Orden',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'center',
      render: (order: number) => (
        <Tag color="blue" style={{ minWidth: '40px', textAlign: 'center' }}>
          {order}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive: boolean, record: PaymentMethod) => (
        <Switch
          checked={isActive}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
          onClick={(checked, e) => {
            e.stopPropagation();
          }}
          onChange={(checked) => handleStatusToggle(record, checked)}
        />
      ),
    },
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code: string) => (
        <Text code style={{ fontSize: '12px' }}>{code}</Text>
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: PaymentMethod) => (
        <Space>
          <div
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: record.color,
              borderRadius: '4px',
              border: '1px solid var(--border-color-strong)',
            }}
          />
          <Text strong={record.isActive}>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => (
        <Text type="secondary">{description || '-'}</Text>
      ),
    },
    {
      title: 'Icono',
      dataIndex: 'iconName',
      key: 'iconName',
      width: 80,
      align: 'center',
      render: (iconName: string) => (
        <Text code style={{ fontSize: '11px' }}>{iconName}</Text>
      ),
    },
    {
      title: 'Configuración',
      key: 'config',
      width: 160,
      render: (_, record: PaymentMethod) => (
        <Space direction="vertical" size="small">
          {record.requiresReference && (
            <Tag color="orange">Requiere Ref.</Tag>
          )}
          {record.requiresAuth && (
            <Tag color="red">Requiere Auth.</Tag>
          )}
          {record.maxAmount && (
            <Tag color="blue">
              Máx: ${record.maxAmount.toFixed(2)}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record: PaymentMethod) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditMethod(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteMethod(record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total de Métodos"
              value={summary?.totalMethods || 0}
              prefix={<SettingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Métodos Activos"
              value={summary?.activeMethods || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Métodos Inactivos"
              value={summary?.inactiveMethods || 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={
          <Space>
            <SettingOutlined />
            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>Formas de Pago</span>
          </Space>
        }
        extra={
          isMobile ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddMethod}
              size="small"
            />
          ) : (
            <Space>
              <Tooltip title="Actualizar datos">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetchMethods()}
                  loading={methodsLoading}
                />
              </Tooltip>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddMethod}
              >
                Nueva Forma de Pago
              </Button>
            </Space>
          )
        }
      >
        {/* Mobile: Extra actions */}
        {isMobile && paymentMethods.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchMethods()}
              loading={methodsLoading}
              block
              size="middle"
            >
              Actualizar datos
            </Button>
          </div>
        )}

        {paymentMethods.length === 0 && !methodsLoading && (
          <Alert
            message="No hay formas de pago configuradas"
            description="Añade una nueva forma de pago para comenzar a recibir pagos."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
            action={
              <Button size="small" type="primary" onClick={handleAddMethod}>
                Nueva Forma de Pago
              </Button>
            }
          />
        )}

        {isMobile && paymentMethods.length > 0 ? (
          // Mobile: List view
          <List
            loading={methodsLoading || summaryLoading}
            dataSource={paymentMethods}
            renderItem={(method) => (
              <List.Item
                style={{
                  padding: '12px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'block'
                }}
              >
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <Tag color="blue" style={{ fontSize: '10px' }}>#{method.displayOrder}</Tag>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: method.color,
                        borderRadius: '3px',
                        border: '1px solid var(--border-color-strong)',
                      }}
                    />
                    <Text strong={method.isActive} style={{ fontSize: '14px' }}>{method.name}</Text>
                  </Space>
                  <Switch
                    size="small"
                    checked={method.isActive}
                    onChange={(checked) => handleStatusToggle(method, checked)}
                  />
                </div>

                {method.description && (
                  <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {method.description}
                  </div>
                )}

                <div style={{ marginBottom: '8px' }}>
                  <Space size="small" wrap>
                    <Text code style={{ fontSize: '10px' }}>{method.code}</Text>
                    {method.requiresReference && <Tag color="orange" style={{ fontSize: '10px' }}>Req. Ref.</Tag>}
                    {method.requiresAuth && <Tag color="red" style={{ fontSize: '10px' }}>Req. Auth.</Tag>}
                    {method.maxAmount && <Tag color="blue" style={{ fontSize: '10px' }}>Máx: ${method.maxAmount.toFixed(2)}</Tag>}
                  </Space>
                </div>

                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditMethod(method)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteMethod(method)}
                  >
                    Eliminar
                  </Button>
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={paymentMethods.map(method => method.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              dataSource={paymentMethods}
              columns={columns}
              rowKey="id"
              loading={methodsLoading || summaryLoading}
              pagination={{
                total: paymentMethods.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} formas de pago`,
              }}
              components={{
                body: {
                  row: SortableTableRow,
                },
              }}
              scroll={{ x: 1000 }}
              size="middle"
            />
          </SortableContext>
        </DndContext>
        )}

        {!isMobile && (
          <>
            <Divider />

            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px' }}>
              <Space direction="vertical">
                <Text type="secondary">
                  Arrastra las filas para reordenar las formas de pago
                </Text>
                <Text type="secondary">
                  El orden afecta cómo se muestran en las pantallas de pago
                </Text>
              </Space>
            </div>
          </>
        )}
      </Card>

      {/* Form Modal */}
      <PaymentMethodFormModal
        visible={formModalVisible}
        onCancel={() => setFormModalVisible(false)}
        onSubmit={handleFormSubmit}
        paymentMethod={editingMethod}
        loading={createLoading || updateLoading}
      />
    </div>
  );
};

export default PaymentMethodsManager;